import { toClassificationDialogData } from '@classification-testing/adapters/classificationResultAdapter';
import type { ClassificationResult } from '@classification-testing/domain';
import { ClassificationType, ContentType } from '@classification-testing/domain';
import { ClassificationDialog } from '@core/components';
import { useSystemConfig } from '@core/hooks/useSystemConfig';
import { getContentDisplayText } from '@core/utils';
import { ContentCard, Text, contentBlurStyles, DefaultUser, Edit, Warning, Icon, IconSize } from '@soteria/design-system-ve';
import type { KeyboardEvent, MouseEvent } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { ClassificationCardControls } from './ClassificationCardControlsV2';
import * as styles from './ClassificationResultItemV2.css';
import { ClassificationHeaderMeta, LabelBadgesSection, PolicyBadgesSection, hasPolicyData } from './MetadataSectionV2';

interface ClassificationResultItemProps {
  result: ClassificationResult;
  _classifiedText: string;
  _promptVersion: string;
}

function getSafetyStatus(result: ClassificationResult) {
  const isSafe = result.classificationType === ClassificationType.SAFE_CONTENT;

  return { isSafe };
}

function getContentInfo(result: ClassificationResult, isSafe: boolean) {
  const content = result.text;
  const preview = content.length > 150 ? `${content.substring(0, 150)}...` : getContentDisplayText(content, result.contentUrl);
  const shouldBlurText = !isSafe && result.text;

  return { preview, shouldBlurText };
}

function ContentPreview({ preview, shouldBlurText, isContentBlurred }: { preview: string; shouldBlurText: string | false; isContentBlurred: boolean }) {
  if (!preview) return null;

  const applyBlur = shouldBlurText && isContentBlurred;

  return <Text className={`${styles.previewText} ${applyBlur ? contentBlurStyles.blurredText : ''}`}>{preview}</Text>;
}

function ImageThumbnail({ imageUrl, altText, isSafe, isContentBlurred }: { imageUrl: string; altText: string; isSafe: boolean; isContentBlurred: boolean }) {
  const [imageError, setImageError] = useState(false);

  if (imageError) {
    return (
      <div aria-label={altText} className={styles.thumbnailContainer} role="img">
        <Text className={styles.thumbnailErrorText}>Image unavailable</Text>
      </div>
    );
  }

  const applyBlur = !isSafe && isContentBlurred;

  return (
    <div className={styles.thumbnailContainer}>
      <img
        alt={altText}
        className={`${styles.thumbnailImage} ${applyBlur ? contentBlurStyles.blurredThumbnail : ''}`}
        src={imageUrl}
        onError={() => setImageError(true)}
      />
    </div>
  );
}

function ContentPreviewArea({
  result,
  isSafe,
  preview,
  shouldBlurText,
  isContentBlurred,
}: {
  result: ClassificationResult;
  isSafe: boolean;
  preview: string;
  shouldBlurText: string | false;
  isContentBlurred: boolean;
}) {
  const hasImageUrl = result.contentType === ContentType.IMAGE && result.contentUrl;

  return (
    <div className={`${styles.contentPreviewSection} ${hasImageUrl ? styles.contentPreviewWithImage : ''}`}>
      {hasImageUrl && (
        <ImageThumbnail
          altText={`Classified image from ${result.timestamp}`}
          imageUrl={result.contentUrl || ''}
          isContentBlurred={isContentBlurred}
          isSafe={isSafe}
        />
      )}
      <div className={hasImageUrl ? styles.contentColumn : ''}>
        <ContentPreview isContentBlurred={isContentBlurred} preview={preview} shouldBlurText={shouldBlurText} />
      </div>
    </div>
  );
}

export function ClassificationResultItemV2({ result }: ClassificationResultItemProps) {
  const [showDialog, setShowDialog] = useState(false);
  const { mediaServiceUrl } = useSystemConfig();
  const { isSafe } = getSafetyStatus(result);
  const [isContentBlurred, setIsContentBlurred] = useState(!isSafe);
  const { preview, shouldBlurText } = getContentInfo(result, isSafe);
  const dialogData = useMemo(() => toClassificationDialogData(result), [result]);

  const hasLabels = result.labels && result.labels.length > 0;
  const hasPolicies = hasPolicyData(result);

  const handleCardClick = () => {
    setShowDialog(true);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setShowDialog(true);
    }
  };

  const handleToggleBlur = (e: MouseEvent) => {
    e.stopPropagation();
    setIsContentBlurred(prev => !prev);
  };

  const handleDialogClose = useCallback(() => setShowDialog(false), []);
  const handleDialogToggleBlur = useCallback(() => setIsContentBlurred(prev => !prev), []);

  const cardClassName = isSafe ? styles.safeContentBorder : styles.unsafeContentBorder;

  return (
    <>
      <ContentCard className={cardClassName} tabIndex={0} onClick={handleCardClick} onKeyDown={handleKeyDown}>
        <ContentCard.Header>
          <ClassificationHeaderMeta result={result} />
          <ClassificationCardControls
            isContentBlurred={isContentBlurred}
            isSafe={isSafe}
            mediaServiceUrl={mediaServiceUrl}
            result={result}
            onToggleBlur={handleToggleBlur}
          />
        </ContentCard.Header>

        <ContentCard.Section hideHeader icon={DefaultUser} title="Content">
          <ContentPreviewArea isContentBlurred={isContentBlurred} isSafe={isSafe} preview={preview} result={result} shouldBlurText={shouldBlurText} />
        </ContentCard.Section>

        <div className={styles.sectionSeparator}>
          {hasPolicies && (
            <div className={styles.inlineSection}>
              <Icon icon={Warning} iconSize={IconSize.XSmall} />
              <Text className={styles.inlineSectionTitle}>Triggered Policies</Text>
              <PolicyBadgesSection result={result} />
            </div>
          )}
        </div>

        <div className={styles.sectionSeparator}>
          {hasLabels && (
            <div className={styles.inlineSection}>
              <Icon icon={Edit} iconSize={IconSize.XSmall} />
              <Text className={styles.inlineSectionTitle}>Labels</Text>
              <LabelBadgesSection result={result} />
            </div>
          )}
        </div>
      </ContentCard>

      <ClassificationDialog
        data={dialogData}
        isContentBlurred={isContentBlurred}
        isOpen={showDialog}
        onClose={handleDialogClose}
        onToggleBlur={handleDialogToggleBlur}
      />
    </>
  );
}
